import React, { useMemo } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, GlobalStyles } from '@mui/material';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { getRoutes } from 'utils/auth';

import { sliceKey, reducer } from './redux/headerSlice/slice';
import { headerSaga } from './redux/headerSlice/saga';
import { selectDealerSelected } from './redux/headerSlice/selectors';
import AppProvider from './globalContexts';
import { Layout } from './features/layout/Layout';
import { HomePage } from './features/home/HomePage';
import { TargetPage } from './features/TargetPage/TargetPage';
import MeasurePage from './features/MeasurePage';
import { AskMiaMeasurePage } from './features/MeasurePage/AskMiaMeasurePageLoadable';
import { GeneratePage } from './features/GeneratePage/GeneratePage';
import { AnalyzePage } from './features/AnalyzePage/AnalyzePage';
import ConfigurePage from './features/ConfigurePage/ConfigurePage';
import { SettingsPage } from './features/SettingsPage/SettingsPage';
import { AdminPage } from './features/AdminPage/AdminPage';
import ContentPage from './features/ContentPage/ContentPage';
import { FeedbackPage } from './features/FeedbackPage/FeedbackPage';
import { NotFoundPage } from './features/NotFoundPage/NotFoundPage';
import DealerSelectionFallback from './sharedComponents/DealerSelectionFallback';
import { routesMap } from 'app/components/AuthRouter/authRoutes';
import ScorecardPage from 'app/pages/ScorecardPage';
import UnsoldPage from 'app/pages/UnsoldPage';
import UnsoldLeadDetailsPage from 'app/pages/UnsoldLeadDetailsPage';
import UserManagementPage from 'app/pages/UserManagementPage';

// Route to component mapping for NewAIAvatarLayout
const aiAvatarRouteMap: Record<string, React.ComponentType<any>> = {
  '/ai-avatar-layout': HomePage,
  '/ai-avatar-layout/measure': MeasurePage,
  '/ai-avatar-layout/measure/ask-mia': AskMiaMeasurePage,
  '/ai-avatar-layout/target': TargetPage,
  '/ai-avatar-layout/generate': GeneratePage,
  '/ai-avatar-layout/analyze': AnalyzePage,
  '/ai-avatar-layout/configure': ConfigurePage,
  '/ai-avatar-layout/content': ContentPage,
  '/ai-avatar-layout/settings': SettingsPage,
  '/ai-avatar-layout/admin': AdminPage,
  '/ai-avatar-layout/feedback': FeedbackPage,
  '/ai-avatar-layout/scorecard': ScorecardPage,
  '/ai-avatar-layout/unsold': UnsoldPage,
  '/ai-avatar-layout/unsold/lead/:leadId': UnsoldLeadDetailsPage,
  '/ai-avatar-layout/user-management': UserManagementPage,
};

// Component name to route mapping (for resource_mappings)
const componentToRouteMap: Record<string, string> = {
  'AIAvatarHomePage': '/ai-avatar-layout',
  'AIAvatarMeasurePage': '/ai-avatar-layout/measure',
  'AIAvatarTargetPage': '/ai-avatar-layout/target',
  'AIAvatarGeneratePage': '/ai-avatar-layout/generate',
  'AIAvatarAnalyzePage': '/ai-avatar-layout/analyze',
  'AIAvatarConfigurePage': '/ai-avatar-layout/configure',
  'AIAvatarContentPage': '/ai-avatar-layout/content',
  'AIAvatarSettingsPage': '/ai-avatar-layout/settings',
  'AIAvatarAdminPage': '/ai-avatar-layout/admin',
  'AIAvatarFeedbackPage': '/ai-avatar-layout/feedback',
  'AIAvatarScorecardPage': '/ai-avatar-layout/scorecard',
  'AIAvatarUnsoldPage': '/ai-avatar-layout/unsold',
  UserManagementPage: '/ai-avatar-layout/user-management',
};


export const AIAvatarLayout: React.FC = () => {
  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  const selectedDealer = useSelector(selectDealerSelected);
  
  // Check if dealer is selected
  const isDealerSelected = selectedDealer && 
    selectedDealer !== 'default' && 
    selectedDealer.dealer && 
    selectedDealer.dealer.trim() !== '';

  // Get routes from resource_mappings
  // Use state to allow re-rendering when routes change
  const [resourceMappings, setResourceMappings] = React.useState<any[]>(() => {
    try {
      return getRoutes() || [];
    } catch (error) {
      console.error('Error getting routes:', error);
      return [];
    }
  });

  // Update routes when localStorage changes
  React.useEffect(() => {
    const updateRoutes = () => {
      try {
        const routes = getRoutes() || [];
        setResourceMappings(routes);
      } catch (error) {
        console.error('Error updating routes:', error);
      }
    };

    // Initial load
    updateRoutes();

    // Listen for storage changes (when user_token is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user_token') {
        updateRoutes();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get allowed routes for NewAIAvatarLayout
  const allowedRoutes = useMemo(() => {
    const allowed: Array<{ path: string; component: React.ComponentType<any> }> = [];
    const seenPaths = new Set<string>();
    
    console.log('🔍 DEBUG: resourceMappings:', resourceMappings);
    console.log('🔍 DEBUG: aiAvatarRouteMap keys:', Object.keys(aiAvatarRouteMap));
    
    // Filter routes that belong to NewAIAvatarLayout
    resourceMappings.forEach((route: any) => {
      const routePath = route.path || '';
      const componentName = route.component || '';
      
      console.log(`🔍 Processing route: path="${routePath}", component="${componentName}"`);
      
      // Check if route is a NewAIAvatarLayout route by path
      if (routePath.startsWith('/ai-avatar-layout')) {
        console.log(`  ✓ Path starts with /ai-avatar-layout`);
        if (!seenPaths.has(routePath)) {
          const component = aiAvatarRouteMap[routePath];
          console.log(`  🔍 Looking up component for "${routePath}":`, component ? 'FOUND' : 'NOT FOUND');
          if (component) {
            allowed.push({ path: routePath, component });
            seenPaths.add(routePath);
            console.log(`  ✅ Added route: ${routePath}`);
          } else {
            console.log(`  ❌ Component not found in aiAvatarRouteMap for path: ${routePath}`);
          }
        }
      }
      
      // Also check by component name if path doesn't match but component is NewAIAvatarLayout component
      if (componentName.startsWith('AIAvatar')) {
        const mappedPath = componentToRouteMap[componentName];
        if (mappedPath) {
          const finalPath = routePath || mappedPath;
          if (!seenPaths.has(finalPath)) {
            const component = routesMap[componentName] || aiAvatarRouteMap[mappedPath];
            if (component) {
              allowed.push({ path: finalPath, component });
              seenPaths.add(finalPath);
            }
          }
        }
      }
    });

    console.log('🔍 DEBUG: Final allowedRoutes:', allowed.map(r => r.path));
    return allowed;
  }, [resourceMappings]);

  const hasUserManagementAccess = useMemo(
    () =>
      resourceMappings.some((route: any) => {
        const routePath = route?.path || '';
        const routeComponent = route?.component || '';
        return (
          routeComponent === 'UserManagementPage' ||
          routePath === '/user-management' ||
          routePath.startsWith('/user-management?')
        );
      }),
    [resourceMappings],
  );

  return (
    <>
      <GlobalStyles
        styles={(theme) => ({
          // Global scrollbar styles for theme compatibility
          '*': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[500],
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
            },
          },
        })}
      />
      <AppProvider>
        <Layout>
          <Switch>
            {/* Feedback page is always accessible, even without dealer selection */}
            <Route exact path="/ai-avatar-layout/feedback" component={FeedbackPage} />
            
            {/* Ask Mia Measure Page route - always available when dealer is selected */}
            {isDealerSelected && (
              <Route exact path="/ai-avatar-layout/measure/ask-mia" component={AskMiaMeasurePage} />
            )}
            
            {/* Unsold Lead Details Page route - always available when dealer is selected */}
            {isDealerSelected && (
              <Route path="/ai-avatar-layout/unsold/lead/:leadId" component={UnsoldLeadDetailsPage} />
            )}
            
            {/* Dynamic routes from resource_mappings - only when dealer is selected */}
            {isDealerSelected && allowedRoutes.length > 0 &&
              allowedRoutes.map((route, index) => {
                // Skip feedback route as it's already handled above
                if (route.path === '/ai-avatar-layout/feedback') {
                  return null;
                }
                return (
                  <Route
                    key={`${route.path}-${index}`}
                    exact
                    path={route.path}
                    component={route.component}
                  />
                );
              })}

            {/* Legacy user-management route mapped to New AI layout path */}
            {isDealerSelected && hasUserManagementAccess && (
              <Route exact path="/ai-avatar-layout/user-management" component={UserManagementPage} />
            )}
            
            {/* Show dealer selection fallback if no dealer selected */}
            {!isDealerSelected && (
              <Route component={DealerSelectionFallback} />
            )}
            
            {/* Show 404 page only when no route matches */}
            <Route component={NotFoundPage} />
          </Switch>
        </Layout>
      </AppProvider>
    </>
  );
};


